import axios from 'axios'
import {
  LoginRequest,
  LoginResponse,
  LoginTwoFactorResponse,
  Verify2FARequest,
  ForgotPasswordRequest,
  TokenStatusResponse,
  RenewTokenResponse,
  ResetPasswordRequest,
  Config2FARequest,
  Config2FAStatus,
  RegisterRequest,
  RegisterAdminRequest,
  UpdatePasswordRequest,
  UpdateUsuarioRequest,
  Usuario,
  Campanha,
  ParticipanteCampanha,
  CampanhaFilters,
  CampanhaStats,
  Participante,
  ParticipanteFilters,
  ParticipanteStats,
  ApiResponse,
  PaginatedResponse,
  ParametroSorteio,
  CreateParametroSorteioRequest,
  UpdateParametroSorteioRequest,
  // tipos de configuracao
  ConfiguracaoSistema,
  UpdateConfiguracaoSistemaRequest,
  TempoSessaoResponse,
  TempoSessaoPublicoResponse,
  ConfiguracoesMap,
  // tipos de lista negativa
  ListaNegativa,
  CreateListaNegativaRequest,
  UpdateListaNegativaRequest,
  VerificarBloqueioResponse,
  ListaNegativaStats,
  ImportacaoJobResponse,
  ImportacaoStatusResponse,
  ParticipanteAnterior,
  CreateParticipanteAnteriorRequest,
  UpdateParticipanteAnteriorRequest,
  ParticipanteAnteriorStats,
  CreateCampanhaRequest,
  UpdateCampanhaRequest,
  // tipos OAuth2
  OAuth2Status,
  OAuth2UrlResponse
} from '@/types'
import { handleApiError } from '../utils/errorHandler'

// Função para detectar a URL base da API automaticamente
function getApiBaseUrl(): string {
  // Se houver variável de ambiente, usar ela
  const envUrl = (import.meta as any).env?.VITE_API_URL
  if (envUrl) {
    console.log('🔧 API URL (variável de ambiente):', envUrl)
    return envUrl
  }
  
  // Detectar automaticamente o hostname/IP atual
  const hostname = window.location.hostname
  const protocol = window.location.protocol
  
  // URLs de produção conhecidas
  const productionDomains = ['sorteios.fenae.org.br', 'fenae-sorteios.azurestaticapps.net']
  const productionBackendUrl = 'https://fenae-sorteios-backend.azurewebsites.net/api'
  
  // Se estiver em produção, usar a URL do backend do Azure
  if (productionDomains.includes(hostname)) {
    console.log('🔧 API URL (produção):', productionBackendUrl, '- Hostname:', hostname)
    return productionBackendUrl
  }
  
  // Se estiver acessando via IP ou domínio não local (desenvolvimento/staging)
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    const port = '8888'
    const apiUrl = `${protocol}//${hostname}:${port}/api`
    console.log('🔧 API URL (detectada por IP/domínio):', apiUrl, '- Hostname:', hostname)
    return apiUrl
  }
  
  // Caso contrário, usar localhost
  const port = '8888'
  const apiUrl = `http://localhost:${port}/api`
  console.log('🔧 API URL (localhost):', apiUrl)
  return apiUrl
}

const apiBaseUrl = getApiBaseUrl()

const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000, // Aumentado para 30 segundos para requisições pela rede
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
    config.headers.Authorization = `Bearer ${token}`
  }
  
  // Se o body é FormData, remover Content-Type para axios definir automaticamente com boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  
  return config
})

// Interceptor para tratamento de erros e refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Detectar erros de rede/CORS (incluindo redirects para URLs externas)
    const isNetworkError = error?.code === 'ERR_NETWORK' || 
                          error?.message?.includes('CORS') ||
                          error?.message?.includes('Network Error') ||
                          (error?.response?.status === 302 && 
                           error?.response?.headers?.location?.includes('accounts.google.com'))

    // Se for erro de rede/CORS, não tentar refresh token
    if (isNetworkError) {
      error._errorHandled = true
      return Promise.reject(error)
    }

    // Tentar refresh token apenas se for 401 e ainda não tentou
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const response = await api.post('/auth/refresh')
        const { token } = response.data
        
        localStorage.setItem('authToken', token)
        originalRequest.headers.Authorization = `Bearer ${token}`
        
        return api(originalRequest)
      } catch (refreshError: any) {
        // Refresh falhou, limpar dados e redirecionar para login
        localStorage.removeItem('authToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        // Marcar refresh error como tratado para não mostrar toast duplicado
        if (refreshError) {
          refreshError._errorHandled = true
        }
        // Redirecionar após um pequeno delay para mostrar possível toast
        setTimeout(() => {
          window.location.href = '/login'
        }, 500)
        return Promise.reject(refreshError)
      }
    }

    // Tratar erro 403 (Forbidden) - redirecionar para /admin se for endpoint de configurações
    if (error.response?.status === 403) {
      const url = originalRequest.url || ''
      if (url.includes('/configuracoes') && !url.includes('/tempo-sessao-publico')) {
        // Marcar erro como tratado para evitar toast duplicado
        error._errorHandled = true
        // Redirecionar para /admin após um pequeno delay
        setTimeout(() => {
          window.location.href = '/admin'
        }, 500)
      } else {
        // Para outros endpoints 403, apenas mostrar mensagem
        handleApiError(error)
      }
    } else if (!(error.response?.status === 401 && originalRequest._retry)) {
      // Não tratar erros de timeout para endpoint de participantes elegíveis (animação do sorteio)
      const url = originalRequest.url || ''
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message?.indexOf('timeout') !== -1
      const isParticipantesElegiveis = url.includes('/participantes-elegiveis-embaralhados')
      
      // Verificar novamente se é erro de rede (pode ter sido detectado depois)
      const isNetworkError = error?.code === 'ERR_NETWORK' || 
                            error?.message?.includes('CORS') ||
                            error?.message?.includes('Network Error')
      
      if (isTimeoutError && isParticipantesElegiveis) {
        // Marcar erro de timeout do sorteio como tratado para não mostrar toast
        error._errorHandled = true
      } else if (isNetworkError) {
        // Não tratar erros de rede - já foram marcados como tratados acima
        error._errorHandled = true
      } else {
        // Tratar erro apenas se não foi tentativa de refresh ou se refresh falhou
        // Não tratar erro 401 aqui se já tentou refresh (será tratado abaixo)
        handleApiError(error)
      }
    } else {
      // Se é 401 após tentar refresh, ainda marca como tratado para evitar duplicação
      error._errorHandled = true
    }

    return Promise.reject(error)
  }
)

// Serviços de Autenticação
export const authService = {
  // Login - pode retornar LoginResponse ou LoginTwoFactorResponse
  async login(credentials: LoginRequest): Promise<LoginResponse | LoginTwoFactorResponse> {
    const response = await api.post('/auth/login', credentials)
    return response.data
  },

  // Solicitar código 2FA para registro
  async solicitarRegistro2FA(data: { email: string; senha: string; nome: string }): Promise<{
    message: string
    email: string
    requiresTwoFactor: boolean
    expiresIn: string
  }> {
    const response = await api.post('/auth/solicitar-registro-2fa', data)
    return response.data
  },

  // Registro de usuário com validação 2FA
  async register(userData: RegisterRequest & { code2FA?: string }): Promise<{
    message: string
    email: string
    aguardandoAprovacao: boolean
    status: string
  }> {
    const response = await api.post('/auth/register', userData)
    return response.data
  },

  // Registro de administrador
  async registerAdmin(adminData: RegisterAdminRequest): Promise<ApiResponse<Usuario>> {
    const response = await api.post('/auth/register-admin', adminData)
    return response.data
  },

  // Refresh token
  async refreshToken(): Promise<ApiResponse<{ token: string }>> {
    const response = await api.post('/auth/refresh')
    return response.data
  },

  // Verificar status do token
  async getTokenStatus(): Promise<TokenStatusResponse> {
    const response = await api.get('/auth/token-status')
    return response.data
  },

  // Renovar token automaticamente
  async renewToken(): Promise<RenewTokenResponse> {
    const response = await api.post('/auth/renew-token')
    return response.data
  },

  // Logout
  async logout(): Promise<ApiResponse<void>> {
    const response = await api.post('/auth/logout')
    return response.data
  },

  // Verificar código 2FA
  async verify2FA(data: Verify2FARequest): Promise<LoginResponse> {
    const response = await api.post('/auth/verify-2fa', data)
    return response.data
  },

  // Esqueci minha senha
  async forgotPassword(data: ForgotPasswordRequest): Promise<ApiResponse<void>> {
    const response = await api.post('/auth/forgot-password', data)
    return response.data
  },

  // Resetar senha
  async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse<void>> {
    const response = await api.post('/auth/reset-password', data)
    return response.data
  },

  // Configurar 2FA
  async config2FA(data: Config2FARequest): Promise<ApiResponse<void>> {
    const response = await api.post('/auth/config-2fa', data)
    return response.data
  },

  // Status 2FA
  async get2FAStatus(): Promise<Config2FAStatus> {
    const response = await api.get('/auth/config-2fa/status')
    const data = response.data
    
    // O backend pode retornar um boolean direto (true/false) ou um objeto { enabled: true/false }
    // Normalizar para sempre retornar um objeto
    if (typeof data === 'boolean') {
      return { enabled: data }
    }
    
    // Se já for um objeto, retornar como está
    if (data && typeof data === 'object' && 'enabled' in data) {
      return { enabled: data.enabled === true }
    }
    
    // Fallback: retornar false se formato desconhecido
    return { enabled: false }
  },

  // OAuth2 - Verificar status
  async verificarOAuth2Status(): Promise<OAuth2Status> {
    const response = await api.get('/auth/oauth2/status')
    return response.data
  },

  // OAuth2 - Obter URL de login
  async obterOAuth2Url(): Promise<OAuth2UrlResponse> {
    const response = await api.get('/auth/oauth2/google/url')
    return response.data
  },

  // OAuth2 - Iniciar login (retorna URL completa para redirecionamento)
  obterUrlLoginGoogle(): string {
    // Obter URL base da API
    const apiUrl = apiBaseUrl.replace('/api', '')
    return `${apiUrl}/oauth2/authorization/google`
  }
}

// Serviços de Usuários
export const usuarioService = {
  // Listar todos os usuários
  async getUsuarios(): Promise<Usuario[]> {
    const response = await api.get('/usuarios')
    return response.data
  },

  // Listar usuários ativos
  async getUsuariosAtivos(): Promise<Usuario[]> {
    const response = await api.get('/usuarios/ativos')
    return response.data
  },

  // Buscar usuário por ID
  async getUsuarioById(id: string): Promise<Usuario> {
    const response = await api.get(`/usuarios/${id}`)
    return response.data
  },

  // Buscar usuário por email
  async getUsuarioByEmail(email: string): Promise<Usuario> {
    const response = await api.get(`/usuarios/email/${email}`)
    return response.data
  },

  // Buscar usuário por CPF
  async getUsuarioByCpf(cpf: string): Promise<Usuario> {
    const response = await api.get(`/usuarios/cpf/${cpf}`)
    return response.data
  },

  // Buscar usuários por nome
  async getUsuariosByNome(nome: string): Promise<Usuario[]> {
    const response = await api.get(`/usuarios/buscar/nome?nome=${nome}`)
    return response.data
  },

  // Buscar usuários por cidade
  async getUsuariosByCidade(cidade: string): Promise<Usuario[]> {
    const response = await api.get(`/usuarios/buscar/cidade?cidade=${cidade}`)
    return response.data
  },

  // Buscar usuários por estado
  async getUsuariosByEstado(estado: string): Promise<Usuario[]> {
    const response = await api.get(`/usuarios/buscar/estado?estado=${estado}`)
    return response.data
  },

  // Criar usuário
  async createUsuario(usuario: any): Promise<Usuario> {
    const response = await api.post('/usuarios', usuario)
    return response.data
  },

  // Atualizar usuário
  async updateUsuario(id: string, usuario: UpdateUsuarioRequest): Promise<Usuario> {
    const response = await api.put(`/usuarios/${id}`, usuario)
    return response.data
  },

  // Atualizar senha do usuário
  async updatePassword(id: string, data: UpdatePasswordRequest): Promise<void> {
    await api.put(`/usuarios/${id}/senha`, data)
  },

  // Alterar senha de usuário (endpoint específico)
  async alterarSenha(id: string, data: { novaSenha: string }): Promise<void> {
    await api.put(`/usuarios/${id}/senha`, data)
  },

  // Alterar status do usuário
  async alterarStatusUsuario(id: string, ativo: boolean): Promise<void> {
    await api.patch(`/usuarios/${id}/status?ativo=${ativo}`)
  },

  // Listar usuários pendentes de aprovação
  async getUsuariosPendentes(): Promise<Array<{
    id: string
    nome: string
    email: string
    role: 'ADMIN' | 'USER'
    ativo: boolean
    dataCriacao: string
  }>> {
    const response = await api.get('/usuarios/pendentes')
    return response.data
  },

  // Ativar usuário (aprovar cadastro)
  async ativarUsuario(id: string): Promise<void> {
    await api.post(`/usuarios/${id}/ativar`)
  },

  // Desativar usuário
  async deactivateUsuario(id: string): Promise<void> {
    await api.patch(`/usuarios/${id}/status?ativo=false`)
  },

  // Remover usuário
  async deleteUsuario(id: string): Promise<void> {
    await api.delete(`/usuarios/${id}`)
  },

  // Contar usuários ativos
  async contarUsuariosAtivos(): Promise<number> {
    const response = await api.get('/usuarios/ativos/count')
    return response.data
  },

  // Contar usuários inativos
  async contarUsuariosInativos(): Promise<number> {
    const response = await api.get('/usuarios/contar/inativos')
    return response.data
  },

  // Listar usuários por status
  async getUsuariosByStatus(status: 'ativo' | 'inativo'): Promise<Usuario[]> {
    const response = await api.get(`/usuarios/status/${status}`)
    return response.data
  },

  // Upload de foto de perfil
  async uploadFotoPerfil(id: string, file: File): Promise<{ mensagem: string; fotoPerfil: string; url: string }> {
    const formData = new FormData()
    formData.append('foto', file)
    
    // Não definir Content-Type manualmente - axios faz isso automaticamente com o boundary correto
    const response = await api.post(`/usuarios/${id}/foto-perfil`, formData, {
      headers: {
        // Axios define automaticamente 'Content-Type: multipart/form-data; boundary=...'
        // Não definir manualmente para evitar problemas no backend
      }
    })
    return response.data
  },

  // Remover foto de perfil
  async removerFotoPerfil(id: string): Promise<{ mensagem: string }> {
    const response = await api.delete(`/usuarios/${id}/foto-perfil`)
    return response.data
  }
}

// Serviços de Campanhas
export const campanhaService = {
  // Listar todas as campanhas
  async getCampanhas(): Promise<Campanha[]> {
    const response = await api.get('/campanhas')
    return response.data
  },

  // Buscar campanha por ID
  async getCampanhaById(id: string): Promise<Campanha> {
    const response = await api.get(`/campanhas/${id}`)
    return response.data
  },

  // Buscar campanhas por ano
  async getCampanhasByAno(ano: string): Promise<Campanha[]> {
    const response = await api.get(`/campanhas/ano/${ano}`)
    return response.data
  },

  // Buscar participantes da campanha
  async getParticipantesCampanha(id: string): Promise<ParticipanteCampanha[]> {
    const response = await api.get(`/campanhas/${id}/participantes`)
    return response.data
  },

  // Contar participantes da campanha
  async contarParticipantesCampanha(id: string): Promise<number> {
    const response = await api.get(`/campanhas/${id}/participantes/contagem`)
    return response.data
  },

  // Buscar participantes por sexo
  async getParticipantesBySexo(id: string, sexo: string): Promise<ParticipanteCampanha[]> {
    const response = await api.get(`/campanhas/${id}/participantes/sexo/${sexo}`)
    return response.data
  },

  // Buscar participantes por UF
  async getParticipantesByUf(id: string, uf: string): Promise<ParticipanteCampanha[]> {
    const response = await api.get(`/campanhas/${id}/participantes/uf/${uf}`)
    return response.data
  },

  // Exportar inscritos da campanha em Excel (.xlsx)
  async exportarInscritosExcel(campanhaId: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await api.get(`/relatorios/excel/inscritos/${campanhaId}`, {
      responseType: 'blob',
      timeout: 60000 // 60 segundos para gerar Excel
    })
    
    // Extrair nome do arquivo do header Content-Disposition
    let fileName = `inscritos_campanha_${campanhaId}_${Date.now()}.xlsx`
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1].replace(/['"]/g, '')
      }
    }
    
    return {
      blob: response.data,
      fileName
    }
  },

  // Buscar campanhas com filtros
  async getCampanhasWithFilters(filters: CampanhaFilters): Promise<Campanha[]> {
    const params = new URLSearchParams()
    if (filters.ano) params.append('ano', filters.ano)
    if (filters.tipo) params.append('tipo', filters.tipo)
    if (filters.abrangencia) params.append('abrangencia', filters.abrangencia)
    if (filters.uf) params.append('uf', filters.uf)
    
    const response = await api.get(`/campanhas?${params.toString()}`)
    return response.data
  },

    // Obter estatísticas das campanhas
    async getCampanhaStats(): Promise<CampanhaStats> {
      const response = await api.get('/campanhas/estatisticas')
      return response.data
    },

    // Buscar campanhas por tipo
    async getCampanhasByTipo(tipo: string): Promise<Campanha[]> {
      const response = await api.get(`/campanhas/tipo/${tipo}`)
      return response.data
    },

    // Buscar campanhas por abrangência
    async getCampanhasByAbrangencia(abrangencia: string): Promise<Campanha[]> {
      const response = await api.get(`/campanhas/abrangencia/${abrangencia}`)
      return response.data
    },

    // Buscar campanhas ativas
    async getCampanhasAtivas(): Promise<Campanha[]> {
      const response = await api.get('/campanhas/ativas')
      return response.data
    },

    // Buscar campanhas por participante
    async getCampanhasByParticipante(participanteId: string): Promise<Campanha[]> {
      const response = await api.get(`/campanhas/participante/${participanteId}`)
      return response.data
    },

    // Listar campanhas inativas (apenas ADMIN)
    async getCampanhasInativas(): Promise<Campanha[]> {
      const response = await api.get('/campanhas/inativas')
      return response.data
    },

    // Criar campanha (apenas ADMIN)
    async createCampanha(data: CreateCampanhaRequest): Promise<Campanha> {
      const response = await api.post('/campanhas', data)
      return response.data
    },

    // Editar campanha (apenas ADMIN)
    async updateCampanha(id: string, data: UpdateCampanhaRequest): Promise<Campanha> {
      const response = await api.put(`/campanhas/${id}`, data)
      return response.data
    },

    // Ativar campanha (apenas ADMIN)
    async ativarCampanha(id: string): Promise<Campanha> {
      const response = await api.patch(`/campanhas/${id}/ativar`)
      return response.data
    },

    // Desativar campanha (apenas ADMIN)
    async desativarCampanha(id: string): Promise<Campanha> {
      const response = await api.patch(`/campanhas/${id}/desativar`)
      return response.data
    }
}

// Serviços de Participantes
export const participanteService = {
  // Listar todos os participantes
  async getParticipantes(page: number = 0, size: number = 10): Promise<PaginatedResponse<Participante>> {
    const response = await api.get(`/participantes?page=${page}&size=${size}`)
    return response.data
  },

  // Buscar participante por ID
  async getParticipanteById(id: string): Promise<Participante> {
    const response = await api.get(`/participantes/${id}`)
    return response.data
  },

  // Buscar por nome
  async getParticipantesByNome(nome: string): Promise<Participante[]> {
    const response = await api.get(`/participantes/buscar/nome?nome=${nome}`)
    return response.data
  },

  // Buscar por CPF
  async getParticipantesByCpf(cpf: string): Promise<Participante[]> {
    const response = await api.get(`/participantes/buscar/cpf?cpf=${cpf}`)
    return response.data
  },

  // Buscar por status de associação
  async getParticipantesByStatusAssociacao(statusAssociacao: string): Promise<Participante[]> {
    const response = await api.get(`/participantes/buscar/status-associacao?statusAssociacao=${statusAssociacao}`)
    return response.data
  },

  // Buscar por campanha (paginado)
  async getParticipantesByCampanha(
    idCampanha: string, 
    page: number = 0, 
    size: number = 10, 
    sortBy: string = 'nome', 
    sortDir: string = 'asc'
  ): Promise<PaginatedResponse<Participante>> {
    const response = await api.get(`/participantes/buscar/campanha?idCampanha=${idCampanha}&page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`, {
      timeout: 60000 // 60 segundos para grandes volumes de dados
    })
    return response.data
  },

  // Buscar por associações
  async getParticipantesByAssociacao(associacao: string): Promise<Participante[]> {
    const response = await api.get(`/participantes/buscar/associacoes?associacao=${associacao}`)
    return response.data
  },

  // Buscar por matrícula
  async getParticipantesByMatricula(matricula: string): Promise<Participante[]> {
    const response = await api.get(`/participantes/buscar/matricula?matricula=${matricula}`)
    return response.data
  },

  // Buscar por sexo
  async getParticipantesBySexo(sexo: string): Promise<Participante[]> {
    const response = await api.get(`/participantes/buscar/sexo?sexo=${sexo}`)
    return response.data
  },

  // Buscar por status
  async getParticipantesByStatus(status: string): Promise<Participante[]> {
    const response = await api.get(`/participantes/buscar/status?status=${status}`)
    return response.data
  },

  // Busca avançada
  async getParticipantesAvancada(filters: ParticipanteFilters): Promise<Participante[]> {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value)
    })
    const response = await api.get(`/participantes/buscar/avancada?${params.toString()}`)
    return response.data
  },

  // Contar total de participantes
  async contarTotalParticipantes(): Promise<number> {
    const response = await api.get('/participantes/contar/total')
    return response.data
  },

  // Contar participantes do dia atual
  async contarParticipantesHoje(): Promise<number> {
    const response = await api.get('/participantes/contar/hoje')
    return response.data
  },

  // Contar por campanha
  async contarParticipantesByCampanha(idCampanha: string): Promise<number> {
    const response = await api.get(`/participantes/contar/campanha?idCampanha=${idCampanha}`)
    return response.data
  },

  // Contar por status
  async contarParticipantesByStatus(status: string): Promise<number> {
    const response = await api.get(`/participantes/contar/status?status=${status}`)
    return response.data
  },

  // Contar por sexo
  async contarParticipantesBySexo(sexo: string): Promise<number> {
    const response = await api.get(`/participantes/contar/sexo?sexo=${sexo}`)
    return response.data
  },

  // Obter estatísticas dos participantes
  async getParticipanteStats(): Promise<ParticipanteStats> {
    const response = await api.get('/participantes/estatisticas')
    return response.data
  },

  // Importar participantes via CSV
  async importarParticipantesCsv(campanhaId: string, arquivo: File): Promise<any> {
    const formData = new FormData()
    formData.append('campanhaId', campanhaId)
    formData.append('arquivo', arquivo)

    const response = await api.post('/participantes/importar', formData, {
      headers: {
        // deixar axios definir boundary automaticamente
      },
      timeout: 60000 // 60s para uploads maiores
    })

    return response.data
  },

  // Criar participante manualmente
  async createParticipante(data: any): Promise<any> {
    const payload = { ...data }
    const response = await api.post('/participantes', payload)
    return response.data
  },

  // Atualizar participante (parcial)
  async updateParticipante(id: string, data: any): Promise<any> {
    const payload = { ...data }
    const response = await api.put(`/participantes/${id}`, payload)
    return response.data
  },

  // Excluir participante
  async deleteParticipante(id: string): Promise<void> {
    await api.delete(`/participantes/${id}`)
  },

  // Excluir participantes em lote
  async deleteParticipantesLote(ids: string[]): Promise<void> {
    await api.delete('/participantes', {
      data: ids,
    })
  }
}

// Serviço para Parâmetros de Sorteio
export const parametroSorteioService = {
  // Listar todos os parâmetros
  async getParametros(): Promise<ParametroSorteio[]> {
    const response = await api.get('/parametros-sorteio')
    return response.data
  },

  // Listar parâmetros ativos
  async getParametrosAtivos(): Promise<ParametroSorteio[]> {
    const response = await api.get('/parametros-sorteio/ativos')
    return response.data
  },

  // Listar parâmetros inativos
  async getParametrosInativos(): Promise<ParametroSorteio[]> {
    const response = await api.get('/parametros-sorteio/inativos')
    return response.data
  },

  // Buscar parâmetro por ID
  async getParametroById(id: string): Promise<ParametroSorteio> {
    const response = await api.get(`/parametros-sorteio/${id}`)
    return response.data
  },

  // Listar parâmetros por campanha
  async getParametrosByCampanha(campanhaId: string): Promise<ParametroSorteio[]> {
    const response = await api.get(`/parametros-sorteio/campanha/${campanhaId}`)
    return response.data
  },

  // Listar parâmetros por ano
  async getParametrosByAno(ano: number): Promise<ParametroSorteio[]> {
    const response = await api.get(`/parametros-sorteio/ano/${ano}`)
    return response.data
  },

  // Buscar por critério de distribuição
  async getParametrosByCriterio(criterio: string): Promise<ParametroSorteio[]> {
    const response = await api.get(`/parametros-sorteio/criterio/${criterio}`)
    return response.data
  },

  // Buscar parâmetros com múltiplas inscrições
  async getParametrosMultiplasInscricoes(): Promise<ParametroSorteio[]> {
    const response = await api.get('/parametros-sorteio/multiplas-inscricoes')
    return response.data
  },

  // Buscar parâmetros válidos
  async getParametrosValidos(): Promise<ParametroSorteio[]> {
    const response = await api.get('/parametros-sorteio/validos')
    return response.data
  },

  // Contar parâmetros ativos
  async contarParametrosAtivos(): Promise<number> {
    const response = await api.get('/parametros-sorteio/contar/ativos')
    return response.data
  },

  // Contar parâmetros inativos
  async contarParametrosInativos(): Promise<number> {
    const response = await api.get('/parametros-sorteio/contar/inativos')
    return response.data
  },

  // Contar parâmetros por campanha
  async contarParametrosByCampanha(campanhaId: string): Promise<number> {
    const response = await api.get(`/parametros-sorteio/contar/campanha/${campanhaId}`)
    return response.data
  },

  // Listar com paginação
  async getParametrosPaginados(
    page: number = 0, 
    size: number = 10, 
    sort: string = 'dataCriacao,desc'
  ): Promise<PaginatedResponse<ParametroSorteio>> {
    const response = await api.get(`/parametros-sorteio/pagina?page=${page}&size=${size}&sort=${sort}`)
    return response.data
  },

  // Criar parâmetro
  async createParametro(data: CreateParametroSorteioRequest, criadoPor: string): Promise<ParametroSorteio> {
    const response = await api.post(`/parametros-sorteio?criadoPor=${criadoPor}`, data)
    return response.data
  },

  // Atualizar parâmetro
  async updateParametro(id: string, data: UpdateParametroSorteioRequest): Promise<ParametroSorteio> {
    const response = await api.put(`/parametros-sorteio/${id}`, data)
    return response.data
  },

  // Alterar status do parâmetro
  async alterarStatusParametro(id: string, ativo: boolean): Promise<ParametroSorteio> {
    const response = await api.patch(`/parametros-sorteio/${id}/status?ativo=${ativo}`)
    return response.data
  },

  // Remover parâmetro
  async deleteParametro(id: string): Promise<void> {
    await api.delete(`/parametros-sorteio/${id}`)
  }
}

// Serviços de Configurações
export const configuracoesService = {
  // Listar todas as configurações
  async getConfiguracoes(): Promise<ConfiguracaoSistema[]> {
    const response = await api.get('/configuracoes')
    return response.data
  },

  // Listar configurações ativas
  async getConfiguracoesAtivas(): Promise<ConfiguracaoSistema[]> {
    const response = await api.get('/configuracoes/ativas')
    return response.data
  },

  // Listar por categoria
  async getConfiguracoesPorCategoria(categoria: string): Promise<ConfiguracaoSistema[]> {
    const response = await api.get(`/configuracoes/categoria/${categoria}`)
    return response.data
  },

  // Buscar por chave
  async getConfiguracaoPorChave(chave: string): Promise<ConfiguracaoSistema> {
    const response = await api.get(`/configuracoes/chave/${chave}`)
    return response.data
  },

  // Obter tempo de sessão atual (requer autenticação ADMIN)
  async getTempoSessao(): Promise<TempoSessaoResponse> {
    const response = await api.get('/configuracoes/tempo-sessao')
    return response.data
  },

  // Obter tempo de sessão público (não requer autenticação)
  async getTempoSessaoPublico(): Promise<TempoSessaoPublicoResponse> {
    const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8888/api'
    const response = await fetch(`${apiUrl}/configuracoes/tempo-sessao-publico`)
    return response.json()
  },

  // Atualizar tempo de sessão (requer ADMIN)
  async updateTempoSessao(minutos: number): Promise<TempoSessaoResponse> {
    const response = await api.put(`/configuracoes/tempo-sessao?minutos=${minutos}`)
    return response.data
  },

  // Atualizar tempo de refresh
  async updateTempoRefresh(minutos: number): Promise<void> {
    await api.put(`/configuracoes/tempo-refresh?minutos=${minutos}`)
  },

  // Criar nova configuração
  async createConfiguracao(data: Omit<ConfiguracaoSistema, 'id' | 'dataCriacao' | 'dataAtualizacao'>): Promise<ConfiguracaoSistema> {
    const response = await api.post('/configuracoes', data)
    return response.data
  },

  // Inicializar configurações padrão
  async inicializarConfiguracoes(): Promise<void> {
    await api.post('/configuracoes/inicializar')
  },

  // Obter configurações como Map
  async getConfiguracoesMap(): Promise<ConfiguracoesMap> {
    const response = await api.get('/configuracoes/map')
    return response.data
  },

  // Atualizar configuração específica (requer ADMIN)
  async updateConfiguracao(id: string, data: UpdateConfiguracaoSistemaRequest): Promise<ConfiguracaoSistema> {
    const response = await api.put(`/configuracoes/${id}`, data)
    return response.data
  }
}

// Utilitários de autenticação
// ===== SERVIÇOS PARA SORTEIOS INDIVIDUAIS =====

export const sorteioService = {
  // Listar sorteios disponíveis
  async getSorteiosDisponiveis(parametroSorteioId: string): Promise<any> {
    const response = await api.get(`/sorteios/disponiveis/${parametroSorteioId}`, {
      timeout: 30000 // 30 segundos para sorteios disponíveis
    })
    return response.data
  },

  // Obter status dos sorteios
  async getStatusSorteios(parametroSorteioId: string): Promise<any> {
    const response = await api.get(`/sorteios/status/${parametroSorteioId}`, {
      timeout: 30000 // 30 segundos para status dos sorteios
    })
    return response.data
  },

  // Validar sorteio específico
  async validarSorteio(parametroSorteioId: string, numeroSorteio: number): Promise<any> {
    const response = await api.get(`/sorteios/validar/${parametroSorteioId}/${numeroSorteio}`, {
      timeout: 30000 // 30 segundos para validação
    })
    return response.data
  },

  // Executar sorteio individual
  async executarSorteioIndividual(parametroSorteioId: string, numeroSorteio: number, executadoPor: string): Promise<any> {
    const response = await api.post(`/sorteios/executar-individual?parametroSorteioId=${parametroSorteioId}&numeroSorteio=${numeroSorteio}&executadoPor=${executadoPor}`, {}, {
      timeout: 60000 // 60 segundos para execução de sorteios
    })
    return response.data
  },

  // ===== SERVIÇOS PARA RESULTADOS DOS SORTEIOS =====

  // Listar campanhas com sorteios
  async getCampanhasComSorteios(): Promise<any> {
    const response = await api.get('/sorteios/campanhas-com-sorteios', {
      timeout: 30000 // 30 segundos para listar campanhas
    })
    return response.data
  },

  // Todos os sorteados de uma campanha
  async getResultadosCampanha(campanhaId: string): Promise<any> {
    const response = await api.get(`/sorteios/resultados/campanha/${campanhaId}`, {
      timeout: 30000 // 30 segundos para resultados da campanha
    })
    return response.data
  },

  // Sorteados de um sorteio específico
  async getResultadosSorteio(execucaoId: string): Promise<any> {
    const response = await api.get(`/sorteios/resultados/sorteio/${execucaoId}`, {
      timeout: 30000 // 30 segundos para resultados do sorteio
    })
    return response.data
  },

  // Lista de sorteios executados por campanha
  async getSorteiosExecutadosCampanha(campanhaId: string): Promise<any> {
    const response = await api.get(`/sorteios/sorteios-executados/campanha/${campanhaId}`, {
      timeout: 30000 // 30 segundos para sorteios executados
    })
    return response.data
  },

  // Relatório PDF básico (resumido)
  async getRelatorioBasico(execucaoId: string): Promise<Blob> {
    const response = await api.get(`/relatorios/pdf/basico/${execucaoId}`, {
      responseType: 'blob',
      timeout: 60000 // 60 segundos para gerar PDF
    })
    return response.data
  },

  // Relatório PDF completo (detalhado)
  async getRelatorioCompleto(execucaoId: string): Promise<Blob> {
    const response = await api.get(`/relatorios/pdf/completo/${execucaoId}`, {
      responseType: 'blob',
      timeout: 60000 // 60 segundos para gerar PDF
    })
    return response.data
  },

  // Exportar sorteados em Excel (.xlsx)
  async exportarSorteadosExcel(execucaoId: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await api.get(`/relatorios/excel/sorteados/${execucaoId}`, {
      responseType: 'blob',
      timeout: 60000 // 60 segundos para gerar Excel
    })
    
    // Extrair nome do arquivo do header Content-Disposition
    let fileName = `sorteados_${execucaoId}_${Date.now()}.xlsx`
    const contentDisposition = response.headers['content-disposition']
    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1].replace(/['"]/g, '')
      }
    }
    
    return {
      blob: response.data,
      fileName
    }
  },

  // Participantes elegíveis embaralhados (para animação do sorteio)
  // Por padrão retorna TODOS os participantes elegíveis (sem limite)
  // Se limite for informado, limita a quantidade retornada
  async getParticipantesElegiveisEmbaralhados(parametroSorteioId: string, limite?: number): Promise<any> {
    const url = limite !== undefined && limite > 0
      ? `/sorteios/parametro/${parametroSorteioId}/participantes-elegiveis-embaralhados?limite=${limite}`
      : `/sorteios/parametro/${parametroSorteioId}/participantes-elegiveis-embaralhados`
    
    const response = await api.get(url, {
      timeout: 60000 // 60 segundos para buscar todos os participantes (aumentado para listas grandes)
    })
    return response.data
  }
}

// Serviço de Lista Negativa
export const listaNegativaService = {
  // Listar todos (paginado)
  async getListaNegativa(page: number = 0, size: number = 10): Promise<PaginatedResponse<ListaNegativa>> {
    const response = await api.get(`/lista-negativa?page=${page}&size=${size}`)
    return response.data
  },

  // Buscar por ID
  async getListaNegativaById(id: string): Promise<ListaNegativa> {
    const response = await api.get(`/lista-negativa/${id}`)
    return response.data
  },

  // Buscar por CPF
  async buscarPorCpf(cpf: string): Promise<ListaNegativa[]> {
    const response = await api.get(`/lista-negativa/buscar/cpf?cpf=${cpf}`)
    return response.data
  },

  // Buscar por Nome
  async buscarPorNome(nome: string): Promise<ListaNegativa[]> {
    const response = await api.get(`/lista-negativa/buscar/nome?nome=${nome}`)
    return response.data
  },

  // Buscar por Status
  async buscarPorStatus(status: 'ATIVO' | 'INATIVO'): Promise<ListaNegativa[]> {
    const response = await api.get(`/lista-negativa/buscar/status?status=${status}`)
    return response.data
  },

  // Buscar por Empresa
  async buscarPorEmpresa(empresa: string): Promise<ListaNegativa[]> {
    const response = await api.get(`/lista-negativa/buscar/empresa?empresa=${empresa}`)
    return response.data
  },

  // Buscar por Cargo
  async buscarPorCargo(cargo: string): Promise<ListaNegativa[]> {
    const response = await api.get(`/lista-negativa/buscar/cargo?cargo=${cargo}`)
    return response.data
  },

  // Buscar Ativos
  async buscarAtivos(): Promise<ListaNegativa[]> {
    const response = await api.get('/lista-negativa/buscar/ativos')
    return response.data
  },

  // Buscar Inativos
  async buscarInativos(): Promise<ListaNegativa[]> {
    const response = await api.get('/lista-negativa/buscar/inativos')
    return response.data
  },

  // Buscar por Motivo
  async buscarPorMotivo(motivo: string): Promise<ListaNegativa[]> {
    const response = await api.get(`/lista-negativa/buscar/motivo?motivo=${motivo}`)
    return response.data
  },

  // Criar Registro
  async criarRegistro(data: CreateListaNegativaRequest): Promise<ListaNegativa> {
    const response = await api.post('/lista-negativa', data)
    return response.data
  },

  // Atualizar Registro
  async atualizarRegistro(id: string, data: UpdateListaNegativaRequest): Promise<ListaNegativa> {
    const response = await api.put(`/lista-negativa/${id}`, data)
    return response.data
  },

  // Excluir Registro
  async excluirRegistro(id: string): Promise<void> {
    await api.delete(`/lista-negativa/${id}`)
  },

  // Alterar Status
  async alterarStatus(id: string, status: 'ATIVO' | 'INATIVO'): Promise<ListaNegativa> {
    const response = await api.patch(`/lista-negativa/${id}/status?status=${status}`)
    return response.data
  },

  // Contar Total
  async contarTotal(): Promise<number> {
    const response = await api.get('/lista-negativa/contar/total')
    return response.data
  },

  // Contar por Status
  async contarPorStatus(status: 'ATIVO' | 'INATIVO'): Promise<number> {
    const response = await api.get(`/lista-negativa/contar/status?status=${status}`)
    return response.data
  },

  // Contar por Empresa
  async contarPorEmpresa(empresa: string): Promise<number> {
    const response = await api.get(`/lista-negativa/contar/empresa?empresa=${empresa}`)
    return response.data
  },

  // Contar por Cargo
  async contarPorCargo(cargo: string): Promise<number> {
    const response = await api.get(`/lista-negativa/contar/cargo?cargo=${cargo}`)
    return response.data
  },

  // Contar Ativos
  async contarAtivos(): Promise<number> {
    const response = await api.get('/lista-negativa/contar/ativos')
    return response.data
  },

  // Contar Inativos
  async contarInativos(): Promise<number> {
    const response = await api.get('/lista-negativa/contar/inativos')
    return response.data
  },

  // Contar por Motivo
  async contarPorMotivo(motivo: string): Promise<number> {
    const response = await api.get(`/lista-negativa/contar/motivo?motivo=${motivo}`)
    return response.data
  },

  // Estatísticas Completas
  async getEstatisticas(): Promise<ListaNegativaStats> {
    const response = await api.get('/lista-negativa/estatisticas')
    return response.data
  },

  // Verificar Bloqueio
  async verificarBloqueio(cpf: string): Promise<VerificarBloqueioResponse> {
    const response = await api.get(`/lista-negativa/verificar-bloqueio?cpf=${cpf}`)
    return response.data
  },

  // Importar lista negativa via CSV (método antigo - mantido para compatibilidade)
  async importarListaNegativa(arquivo: File): Promise<{
    totalProcessados: number
    totalSucessos: number
    totalErros: number
    erros: string[]
    sucesso: boolean
  }> {
    const formData = new FormData()
    formData.append('arquivo', arquivo)

    const response = await api.post('/lista-negativa/importar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 segundos para upload de arquivo
    })

    return response.data
  },

  // Importar lista negativa via CSV (método assíncrono - novo)
  async importarListaNegativaAsync(arquivo: File): Promise<ImportacaoJobResponse> {
    const formData = new FormData()
    formData.append('arquivo', arquivo)

    const response = await api.post('/lista-negativa/importar-async', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 10000, // Timeout curto, pois retorna jobId imediatamente
    })

    return response.data
  },

  // Verificar status da importação
  async verificarStatusImportacao(jobId: string): Promise<ImportacaoStatusResponse> {
    const response = await api.get(`/lista-negativa/importar-status/${jobId}`)
    return response.data
  },

  // Solicitar código 2FA para limpeza
  async solicitarLimpeza(motivo: string): Promise<{
    message: string
    email: string
    expiresIn: string
    codigoEnviado: boolean
  }> {
    const response = await api.post('/lista-negativa/solicitar-limpeza', { motivo })
    return response.data
  },

  // Confirmar limpeza com código 2FA
  async confirmarLimpeza(codigo: string, motivo: string): Promise<{
    sucesso: boolean
    mensagem: string
    registrosRemovidos: number
    usuario: string
    email: string
    motivo: string
    timestamp: string
  }> {
    const response = await api.delete('/lista-negativa/limpar-tudo', {
      data: { codigo, motivo }
    })
    return response.data
  }
}

// Serviço de Monitoramento e Auditoria
export const monitoramentoService = {
  // Sessões Ativas
  async getSessoesAtivas(): Promise<{
    sessoes: Array<{
      id: string
      usuarioId: string
      usuarioEmail: string
      usuarioNome: string
      role: 'ADMIN' | 'USER'
      ipAddress: string
      userAgent: string
      dataLogin: string
      ultimaAtividade: string
      ativo: boolean
    }>
    total: number
    totalAdmins: number
    totalUsers: number
  }> {
    const response = await api.get('/sessoes')
    return response.data
  },

  async getSessoesPorRole(role: 'ADMIN' | 'USER'): Promise<Array<{
    id: string
    usuarioEmail: string
    usuarioNome: string
    role: 'ADMIN' | 'USER'
    ipAddress: string
    dataLogin: string
    ultimaAtividade: string
  }>> {
    const response = await api.get(`/sessoes/role/${role}`)
    return response.data
  },

  async getEstatisticasSessoes(): Promise<{
    total: number
    totalAdmins: number
    totalUsers: number
  }> {
    const response = await api.get('/sessoes/estatisticas')
    return response.data
  },

  // Eventos da Aplicação
  async getEventos(params?: {
    page?: number
    size?: number
    sortBy?: string
    sortDir?: 'ASC' | 'DESC'
  }): Promise<{
    content: Array<{
      id: string
      usuarioId?: string
      usuarioEmail?: string
      usuarioNome?: string
      role?: 'ADMIN' | 'USER'
      tipoEvento: string
      acao: string
      endpoint: string
      metodoHttp: string
      ipAddress?: string
      userAgent?: string
      referer?: string
      statusCode?: number
      tempoRespostaMs?: number
      detalhes?: Record<string, any>
      dataEvento: string
      sessaoId?: string
    }>
    totalElements: number
    totalPages: number
    number: number
    size: number
  }> {
    const queryParams = new URLSearchParams()
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params?.sortDir) queryParams.append('sortDir', params.sortDir)
    
    const response = await api.get(`/eventos?${queryParams.toString()}`)
    return response.data
  },

  async getEventosPorUsuario(usuarioId: string, params?: {
    page?: number
    size?: number
  }): Promise<{
    content: Array<any>
    totalElements: number
    totalPages: number
    number: number
    size: number
  }> {
    const queryParams = new URLSearchParams()
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    
    const response = await api.get(`/eventos/usuario/${usuarioId}?${queryParams.toString()}`)
    return response.data
  },

  async getEventosPorTipo(tipoEvento: string, params?: {
    page?: number
    size?: number
  }): Promise<{
    content: Array<any>
    totalElements: number
    totalPages: number
    number: number
    size: number
  }> {
    const queryParams = new URLSearchParams()
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    
    const response = await api.get(`/eventos/tipo/${tipoEvento}?${queryParams.toString()}`)
    return response.data
  },

  async getEventosPorEndpoint(endpoint: string, params?: {
    page?: number
    size?: number
  }): Promise<{
    content: Array<any>
    totalElements: number
    totalPages: number
    number: number
    size: number
  }> {
    const queryParams = new URLSearchParams()
    queryParams.append('endpoint', endpoint)
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    
    const response = await api.get(`/eventos/endpoint?${queryParams.toString()}`)
    return response.data
  },

  async getEventosPorPeriodo(dataInicio: string, dataFim: string, params?: {
    page?: number
    size?: number
  }): Promise<{
    content: Array<any>
    totalElements: number
    totalPages: number
    number: number
    size: number
  }> {
    const queryParams = new URLSearchParams()
    queryParams.append('dataInicio', dataInicio)
    queryParams.append('dataFim', dataFim)
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    
    const response = await api.get(`/eventos/periodo?${queryParams.toString()}`)
    return response.data
  },

  async getEventosPorUsuarioEPeriodo(usuarioId: string, dataInicio: string, dataFim: string, params?: {
    page?: number
    size?: number
  }): Promise<{
    content: Array<any>
    totalElements: number
    totalPages: number
    number: number
    size: number
  }> {
    const queryParams = new URLSearchParams()
    queryParams.append('dataInicio', dataInicio)
    queryParams.append('dataFim', dataFim)
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    
    const response = await api.get(`/eventos/usuario/${usuarioId}/periodo?${queryParams.toString()}`)
    return response.data
  }
}

// Serviço de Participantes Anteriores
export const participantesAnterioresService = {
  // Listar todos os participantes anteriores (paginação)
  async getParticipantesAnteriores(page: number = 0, size: number = 10): Promise<PaginatedResponse<ParticipanteAnterior>> {
    const response = await api.get(`/participantes-anteriores?page=${page}&size=${size}`)
    return response.data
  },

  // Buscar por ID
  async getParticipanteAnteriorById(id: string): Promise<ParticipanteAnterior> {
    const response = await api.get(`/participantes-anteriores/${id}`)
    return response.data
  },

  // Buscar por CPF
  async buscarPorCpf(cpf: string): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/cpf?cpf=${cpf}`)
    return response.data
  },

  // Buscar por Nome
  async buscarPorNome(nome: string): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/nome?nome=${nome}`)
    return response.data
  },

  // Buscar por Status
  async buscarPorStatus(status: 'ATIVO' | 'INATIVO'): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/status?status=${status}`)
    return response.data
  },

  // Buscar por Edição
  async buscarPorEdicao(edicao: string): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/edicao?edicao=${edicao}`)
    return response.data
  },

  // Buscar por Mínimo de Participações
  async buscarPorMinimoParticipacoes(minParticipacoes: number): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/minimo-participacoes?minParticipacoes=${minParticipacoes}`)
    return response.data
  },

  // Buscar por APCEF
  async buscarPorAPCEF(apcef: string): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/apcef?apcef=${apcef}`)
    return response.data
  },

  // Buscar Aposentados
  async buscarAposentados(): Promise<ParticipanteAnterior[]> {
    const response = await api.get('/participantes-anteriores/buscar/aposentados')
    return response.data
  },

  // Buscar Pensionistas
  async buscarPensionistas(): Promise<ParticipanteAnterior[]> {
    const response = await api.get('/participantes-anteriores/buscar/pensionistas')
    return response.data
  },

  // Buscar Aposentados ou Pensionistas
  async buscarAposentadosOuPensionistas(): Promise<ParticipanteAnterior[]> {
    const response = await api.get('/participantes-anteriores/buscar/aposentados-ou-pensionistas')
    return response.data
  },

  // Buscar Ativos
  async buscarAtivos(): Promise<ParticipanteAnterior[]> {
    const response = await api.get('/participantes-anteriores/buscar/ativos')
    return response.data
  },

  // Buscar por Qtd Edições Inspira
  async buscarPorQtdEdicoesInspira(qtdEdicoes: number): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/qtd-edicoes-inspira?qtdEdicoes=${qtdEdicoes}`)
    return response.data
  },

  // Buscar com Mínimo de Edições Inspira
  async buscarPorMinEdicoesInspira(minEdicoes: number): Promise<ParticipanteAnterior[]> {
    const response = await api.get(`/participantes-anteriores/buscar/min-edicoes-inspira?minEdicoes=${minEdicoes}`)
    return response.data
  },

  // Buscar Elegíveis para Sorteio
  async buscarElegiveis(): Promise<ParticipanteAnterior[]> {
    const response = await api.get('/participantes-anteriores/elegiveis')
    return response.data
  },

  // Criar participante anterior
  async criarParticipanteAnterior(data: CreateParticipanteAnteriorRequest): Promise<ParticipanteAnterior> {
    const response = await api.post('/participantes-anteriores', data)
    return response.data
  },

  // Atualizar participante anterior
  async atualizarParticipanteAnterior(id: string, data: UpdateParticipanteAnteriorRequest): Promise<ParticipanteAnterior> {
    const response = await api.put(`/participantes-anteriores/${id}`, data)
    return response.data
  },

  // Excluir participante anterior
  async excluirParticipanteAnterior(id: string): Promise<void> {
    await api.delete(`/participantes-anteriores/${id}`)
  },

  // Alterar status
  async alterarStatus(id: string, status: 'ATIVO' | 'INATIVO'): Promise<ParticipanteAnterior> {
    const response = await api.patch(`/participantes-anteriores/${id}/status?status=${status}`)
    return response.data
  },

  // Adicionar edição
  async adicionarEdicao(id: string, edicao: string): Promise<ParticipanteAnterior> {
    const response = await api.post(`/participantes-anteriores/${id}/edicoes?edicao=${edicao}`)
    return response.data
  },

  // Remover edição
  async removerEdicao(id: string, edicao: string): Promise<ParticipanteAnterior> {
    const response = await api.delete(`/participantes-anteriores/${id}/edicoes?edicao=${edicao}`)
    return response.data
  },

  // Contar total
  async contarTotal(): Promise<number> {
    const response = await api.get('/participantes-anteriores/contar/total')
    if (response.data === undefined || response.data === null) return 0
    if (typeof response.data === 'number') return response.data
    return (response.data as any)?.total ?? (response.data as any)?.count ?? 0
  },

  // Contar por status
  async contarPorStatus(status: 'ATIVO' | 'INATIVO'): Promise<number> {
    const response = await api.get(`/participantes-anteriores/contar/status?status=${status}`)
    if (response.data === undefined || response.data === null) return 0
    if (typeof response.data === 'number') return response.data
    return (response.data as any)?.total ?? (response.data as any)?.ativos ?? (response.data as any)?.inativos ?? (response.data as any)?.count ?? 0
  },

  // Contar por edição
  async contarPorEdicao(edicao: string): Promise<number> {
    const response = await api.get(`/participantes-anteriores/contar/edicao?edicao=${edicao}`)
    return typeof response.data === 'number' ? response.data : (response.data as any)?.total || 0
  },

  // Estatísticas
  async getEstatisticas(): Promise<ParticipanteAnteriorStats> {
    const response = await api.get('/participantes-anteriores/estatisticas')
    return response.data
  },

  // Calcular reserva de 10%
  async calcularReserva(totalVagas: number): Promise<number> {
    const response = await api.get(`/participantes-anteriores/calcular-reserva?totalVagas=${totalVagas}`)
    return response.data
  },

  // Importar lista negativa via CSV (método assíncrono - novo)
  async importarParticipantesAnterioresAsync(arquivo: File): Promise<ImportacaoJobResponse> {
    const formData = new FormData()
    formData.append('arquivo', arquivo)

    const response = await api.post('/participantes-anteriores/importar-async', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 10000, // Timeout curto, pois retorna jobId imediatamente
    })

    return response.data
  },

  // Verificar status da importação
  async verificarStatusImportacao(jobId: string): Promise<ImportacaoStatusResponse> {
    const response = await api.get(`/participantes-anteriores/importar-status/${jobId}`)
    return response.data
  },

  // Limpar tudo
  async limparTudo(): Promise<{
    sucesso: boolean
    mensagem: string
    registrosRemovidos: number
  }> {
    const response = await api.delete('/participantes-anteriores/limpar-tudo')
    return response.data
  }
}

export const authUtils = {
  // Salvar dados de autenticação
  saveAuthData(loginResponse: LoginResponse) {
    localStorage.setItem('authToken', loginResponse.token)
    localStorage.setItem('refreshToken', loginResponse.refreshToken)
    localStorage.setItem('user', JSON.stringify(loginResponse.usuario))
  },

  // Limpar dados de autenticação
  clearAuthData() {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
  },

  // Obter token atual
  getToken(): string | null {
    const token = localStorage.getItem('authToken')
    if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
      return null
    }
    return token
  },

  // Obter refresh token
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken')
  },

  // Obter usuário atual
  getCurrentUser(): Usuario | null {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  },

  // Verificar se está autenticado
  isAuthenticated(): boolean {
    return !!this.getToken()
  },

  // Verificar se é admin
  isAdmin(): boolean {
    const user = this.getCurrentUser()
    return user?.role === 'ADMIN'
  },

  // Verificar se é usuário
  isUser(): boolean {
    const user = this.getCurrentUser()
    return user?.role === 'USER'
  }
}

// Serviço de APIs de IA
export const aiService = {
  // Gerar descrição de campanha usando IA
  async gerarDescricaoCampanha(campanhaId: string): Promise<string> {
    try {
      const response = await api.post<{ campanhaId: string; descricao: string }>(
        `/ai/campanhas/${campanhaId}/gerar-descricao`
      )
      return response.data.descricao
    } catch (error) {
      handleApiError(error)
      throw error
    }
  }
}

export default api
